import ProjectService from '../services/project.service'
import type { Workspace } from '../context/workspace.context'

/** Достаёт id рабочего пространства из объекта проекта в любом из форматов бэка. */
export function getProjectWorkspaceId(project: any): string | null {
    const workspaceId = project?.workspace_id
        ?? project?.workspace?.id
        ?? project?.project?.workspace_id
        ?? project?.project?.workspace?.id

    return workspaceId == null ? null : String(workspaceId)
}

const projectWorkspaceCache = new Map<string, string | null>()

/** Узнаёт пространство проекта по id, кешируя ответ на время сессии. */
export async function fetchProjectWorkspaceId(projectId: string | number): Promise<string | null> {
    const cacheKey = String(projectId)

    if (projectWorkspaceCache.has(cacheKey)) {
        return projectWorkspaceCache.get(cacheKey) ?? null
    }

    try {
        const workspaceId = getProjectWorkspaceId(await ProjectService.get(cacheKey))
        projectWorkspaceCache.set(cacheKey, workspaceId)
        return workspaceId
    } catch (error) {
        console.error('Не удалось определить рабочее пространство проекта:', error)
        return null
    }
}

interface EnsureWorkspaceOptions {
    workspaceId: string | number | null | undefined
    currentWorkspace: Workspace | null
    workspaces: Workspace[]
    setWorkspace: (workspace: Workspace) => void
    /** Перезапрашивает список пространств, если нужного нет в уже загруженном. */
    fetchWorkspaces?: () => Promise<Workspace[] | void> | void
}

export type EnsureWorkspaceResult = 'unchanged' | 'switched' | 'not-found'

/**
 * Переключает активное рабочее пространство на то, которому принадлежит открываемая
 * сущность. Нужно для переходов по внешним ссылкам: пользователь может открыть ссылку
 * на проект/задачу, находясь в другом пространстве, и тогда без переключения страница
 * не находит данные.
 *
 * Если пространства нет в загруженном списке (его добавили уже после старта сессии),
 * список перезапрашивается один раз.
 */
export async function ensureWorkspaceForEntity({
    workspaceId,
    currentWorkspace,
    workspaces,
    setWorkspace,
    fetchWorkspaces,
}: EnsureWorkspaceOptions): Promise<EnsureWorkspaceResult> {
    if (workspaceId == null) {
        return 'unchanged'
    }

    const targetId = String(workspaceId)

    if (String(currentWorkspace?.id) === targetId) {
        return 'unchanged'
    }

    const findTarget = (list: Workspace[]) => list.find((item) => String(item.id) === targetId)
    let targetWorkspace = findTarget(workspaces)

    if (!targetWorkspace && fetchWorkspaces) {
        const refreshed = await fetchWorkspaces()

        if (Array.isArray(refreshed)) {
            targetWorkspace = findTarget(refreshed)
        }
    }

    if (!targetWorkspace) {
        return 'not-found'
    }

    setWorkspace(targetWorkspace)

    return 'switched'
}
